import omit from 'lodash.omit';
import { datadogRum } from '@datadog/browser-rum';
import { telemetryUserAttributes, addAppSpecificData } from '@/utilities/analytics';
import { pendoReadyPromise } from '@/utilities/resolvers/pendoReadyResolver';

const PENDO_OMITTED_FIELDS = ['build_date', 'srp_refresh', 'user_id', 'vue_spa', 'web_version'];

export const actions = {
  log({ rootState }, message) {
    if (import.meta.env.DEV && rootState.devTools.analyticsLoggingEnabled) {
      window.console.info(`Analytics - EVENT: ${message}`);
    }
  },
  noticeError({ dispatch }, { error, customAttributes = {} }) {
    const errorMessage = error.message || 'NO MESSAGE';
    dispatch('log', `Error Noticed - MESSAGE: ${errorMessage}, DATA: ${JSON.stringify(customAttributes)}`);
    if (import.meta.env.VITE_THIRD_PARTY_JS_ENABLED === 'true') {
      datadogRum.addError(error, customAttributes);
    }
  },
  track({ dispatch, rootGetters, rootState }, {
    title, data, withPendo = false, withDatadog = true, withMixpanel = true,
  } = {}) {
    const userId = data?.userId || data?.user_id
    || telemetryUserAttributes(rootGetters.currentUser)?.user_id;

    const compiledData = {
      user_id: userId,
      ...data,
    };

    dispatch('log', `${title}, DATA: ${JSON.stringify(compiledData)}`);
    if (withDatadog) {
      dispatch('trackDatadog', { title, compiledData });
    }

    const featureFlagSlugs = rootState.featureFlags?.featureFlagSlugs;
    const pendoFlagSlug = featureFlagSlugs?.enableTrackPendoInsteadOfMixpanel;
    const pendoFlagEnabled = rootState.featureFlags?.flags?.[pendoFlagSlug];
    if (withPendo && pendoFlagEnabled) {
      dispatch('trackPendo', { title, compiledData });
    }

    if (withMixpanel) {
      dispatch('trackMixPanel', { title, compiledData });
    }
  },
  trackDatadog(_context, { title, compiledData = {} }) {
    if (import.meta.env.VITE_THIRD_PARTY_JS_ENABLED === 'true') {
      const localData = addAppSpecificData(compiledData);
      datadogRum.addAction(title, localData);
    }
  },
  trackMixPanel(_context, { title, compiledData = {} }) {
    const localData = addAppSpecificData(compiledData);
    if (window.mixpanel) {
      window.mixpanel.track(title, localData);
    }
  },
  async trackPendo(_context, { title, compiledData = {} }) {
    const localData = addAppSpecificData(compiledData);
    await pendoReadyPromise;
    if (window.pendo) {
      window.pendo.track(title, omit(localData, PENDO_OMITTED_FIELDS));
    }
  },
  resetMixpanel({ dispatch }) {
    if (window.mixpanel) {
      window.mixpanel.reset();
      dispatch('log', `No longer tracking for User: ${JSON.stringify(telemetryUserAttributes)}`);
    }
  },
};

export default {
  namespaced: true,
  actions,
};
